import ConfirmDialog from "components/ConfirmDialog"
import ItemCover from "components/ItemCover"
import NoItems from "components/NoItems"
import PageLoader from "components/PageLoader"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { getTintVTOService } from "services/tintvto"
import { Pencil, Plus, Trash } from "lucide-react"
import { useAlert } from "providers/AlertProvider"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"

interface LookSummary {
  cover: string
  id: string
  name: string
  products: {
    selectedSkus: unknown[]
  }
  visible: boolean
}

export const LooksPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const service = getTintVTOService()
  const [looks, setLooks] = useState<LookSummary[]>([])
  const [isLoading, setLoading] = useState(false)
  const [visibilityLoading, setVisibilityLoading] = useState<number | null>(null)
  const [lookDelete, setLookDelete] = useState<LookSummary | null>(null)
  const { showAlert } = useAlert()

  const updateLooks = async () => {
    setLoading(true)
    const { data } = await service.looks()

    setLooks(data?.allLooks || [])
    setLoading(false)
  }

  useEffect(() => {
    updateLooks()
  }, [])

  const toggleVisibility = async (target: LookSummary, index: number) => {
    setVisibilityLoading(index)
    await service.toggleLookVisibility(target.id, !target.visible)
    await updateLooks()
    showAlert({
      error: false,
      text: t("looksPage.visibilityUpdated", {
        name: looks[index].name,
        status: target.visible ? t("looksPage.hidden") : t("looksPage.visible"),
      }),
    })
    setVisibilityLoading(null)
  }

  return (
    <>
      <ConfirmDialog
        isOpen={!!lookDelete}
        onClose={() => setLookDelete(null)}
        onConfirm={async () => {
          if (!lookDelete) return
          await service.deleteLook(lookDelete?.id)
          setLookDelete(null)
          showAlert({
            error: false,
            text: t("looksPage.deleteSuccess", { name: lookDelete?.name }),
          })
          await updateLooks()
        }}
        text={t("looksPage.deleteConfirmation", { name: lookDelete?.name })}
      />
      <div className="p-6" data-testid="looks-page">
        {/* Page header */}
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight" data-testid="looks-title">
              {t("looksPage.title")}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("looksPage.subtitle", { count: looks.length })}
            </p>
          </div>
          <Button size="sm" asChild>
            <a href="/looks/add" data-testid="looks-add">
              <Plus className="mr-1.5 h-3.5 w-3.5" />
              {t("looksPage.addLook")}
            </a>
          </Button>
        </div>

        {/* Table */}
        {!!looks.length && (
          <Card data-testid="looks-table">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="font-medium">{t("looksPage.logo")}</TableHead>
                  <TableHead className="font-medium">{t("looksPage.name")}</TableHead>
                  <TableHead className="font-medium">{t("looksPage.products")}</TableHead>
                  <TableHead className="font-medium"></TableHead>
                  <TableHead className="font-medium"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {looks.map((look, index) => (
                  <TableRow key={index} data-testid="look-row" data-look-id={look.id}>
                    <TableCell>
                      <div className="p-1">
                        <ItemCover cover={look.cover} name={look.name} size={64} />
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-foreground">{look.name}</span>
                    </TableCell>
                    <TableCell>
                      {t("looksPage.productsCount", { count: look.products.selectedSkus.length })}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          data-testid="look-visibility"
                          disabled={visibilityLoading === index}
                          checked={look.visible}
                          onCheckedChange={() => toggleVisibility(look, index)}
                        />
                        <Label className="text-sm">
                          {look.visible ? t("looksPage.visible") : t("looksPage.hidden")}
                        </Label>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setLookDelete(look)}
                          aria-label={t("looksPage.delete")}
                          data-testid="look-delete"
                        >
                          <Trash className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => navigate(`/looks/edit?id=${look.id}`)}
                          aria-label={t("looksPage.edit")}
                          data-testid="look-edit"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
        {isLoading && <PageLoader />}
        {!isLoading && !looks.length && (
          <div
            className="flex items-center justify-center rounded-lg border border-dashed p-12"
            data-testid="looks-empty-state"
          >
            <NoItems
              text={t("looksPage.noLooks")}
              addText={t("looksPage.addFirstLook")}
              addLink={`/looks/add`}
              showAdd
            />
          </div>
        )}
      </div>
    </>
  )
}
